import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Ban, BadgePercent, ChevronDown, ChevronRight, History, Link as LinkIcon, Minus, Plus, RefreshCw, Save, Search, Send } from 'lucide-react'
import {
	addAccountScopes,
	cancelAccountBoletosOnQiTech,
	consultTransactionOnQiTech,
	createPayingAgentAccount,
	fetchAccountAuthClient,
	fetchAccountById,
	fetchAuthScopes,
	fetchBillingConfiguration,
	fetchOnboardingPjById,
	fetchPixLimits,
	fetchPixLimitsRequests,
	finalizeOnboarding,
	generateBiometricLink,
	removeAccountBoletoDiscountsOnQiTech,
	removeAccountScopes,
	requestPixLimitChange,
	syncAccountBaasBalance,
	syncAccountDailyBalances,
	updateBillingConfiguration,
} from '../../services/api'
import Loader from '../../components/Loader'
import Pagination from '../../components/Pagination'
import {
	Alert,
	Badge,
	Button,
	Card,
	CopyButton,
	CopyableId,
	DetailGrid,
	DetailItem,
	Drawer,
	EmptyState,
	JsonViewer,
	PageHeader,
	Select,
	TextInput,
	formatCurrency,
	formatDateTime,
	formatDocument,
	formatPercentage,
	statusTone,
} from '../../components/ui'

const BILLING_FEE_GROUPS = [
	{
		key: 'bankSlip',
		wireKey: 'bank_slip',
		title: 'Boleto',
		fees: [
			{ key: 'registration', wireKey: 'registration', label: 'Registro' },
			{ key: 'bankSlipInstantRegistration', wireKey: 'bank_slip_instant_registration', label: 'Registro instantaneo' },
			{ key: 'permanence', wireKey: 'permanence', label: 'Permanencia' },
			{ key: 'protestRemoval', wireKey: 'protest_removal', label: 'Remocao de protesto' },
			{ key: 'protestRequest', wireKey: 'protest_request', label: 'Solicitacao de protesto' },
			{ key: 'protestCosts', wireKey: 'protest_costs', label: 'Custas de protesto' },
			{ key: 'expirationDateChange', wireKey: 'expiration_date_change', label: 'Alteracao de data de vencimento' },
			{ key: 'rebateInclusion', wireKey: 'rebate_inclusion', label: 'Inclusao de abatimento' },
			{ key: 'discountInclusion', wireKey: 'discount_inclusion', label: 'Inclusao de desconto' },
			{ key: 'notaryOfficePayment', wireKey: 'notary_office_payment', label: 'Pagamento em cartorio' },
			{ key: 'expirationWriteOff', wireKey: 'expiration_write_off', label: 'Baixa por vencimento' },
			{ key: 'writeOff', wireKey: 'write_off', label: 'Baixa' },
			{ key: 'protestWriteOff', wireKey: 'protest_write_off', label: 'Baixa de protesto' },
			{ key: 'protestRemovalAndWriteOff', wireKey: 'protest_removal_and_write_off', label: 'Remocao e baixa de protesto' },
			{ key: 'payment', wireKey: 'payment', label: 'Pagamento' },
			{ key: 'paymentQrCode', wireKey: 'payment_qr_code', label: 'Pagamento via QR Code' },
			{ key: 'fineOrInterestInclusion', wireKey: 'fine_or_interest_inclusion', label: 'Inclusao de multa ou juros' },
			{ key: 'bankSlipFineAlteration', wireKey: 'bank_slip_fine_alteration', label: 'Alteracao de multa do boleto' },
			{ key: 'bankSlipInterestAlteration', wireKey: 'bank_slip_interest_alteration', label: 'Alteracao de juros do boleto' },
			{ key: 'bankSlipDiscountAlteration', wireKey: 'bank_slip_discount_alteration', label: 'Alteracao de desconto do boleto' },
		],
	},
	{
		key: 'ted',
		wireKey: 'ted',
		title: 'TED',
		fees: [
			{ key: 'incomingTed', wireKey: 'incoming_ted', label: 'TED recebida' },
			{ key: 'outgoingTed', wireKey: 'outgoing_ted', label: 'TED enviada' },
		],
	},
	{
		key: 'pixTransfer',
		wireKey: 'pix_transfer',
		title: 'Pix',
		fees: [
			{ key: 'incomingPix', wireKey: 'incoming_pix', label: 'Pix recebido' },
			{ key: 'outgoingPix', wireKey: 'outgoing_pix', label: 'Pix enviado' },
		],
	},
	{
		key: 'account',
		wireKey: 'account',
		title: 'Conta',
		fees: [
			{ key: 'accountMaintenance', wireKey: 'account_maintenance', label: 'Manutencao de conta' },
			{ key: 'accountCreation', wireKey: 'account_creation', label: 'Abertura de conta' },
			{ key: 'incomingFunds', wireKey: 'incoming_funds', label: 'Recebimento de recursos' },
		],
	},
	{
		key: 'qrCode',
		wireKey: 'qr_code',
		title: 'QR Code',
		fees: [{ key: 'dynamicQrCodeExpiration', wireKey: 'dynamic_qr_code_expiration', label: 'Expiracao de QR Code dinamico' }],
	},
	{
		key: 'automaticPix',
		wireKey: 'automatic_pix',
		title: 'Pix Automatico',
		fees: [
			{ key: 'activeRecurrence', wireKey: 'active_recurrence', label: 'Recorrencia ativa' },
			{ key: 'recurrenceSettlement', wireKey: 'recurrence_settlement', label: 'Liquidacao de recorrencia' },
		],
	},
]

const emptyFee = () => ({ amount: '', expenseType: '' })

// Constroi o estado local do formulario a partir da resposta do GET. A API usa
// snake_case (@JsonProperty explicito no backend, sem naming strategy) - le pelo
// wireKey de cada grupo/tarifa e guarda no estado usando o key (camelCase) local.
const buildBillingFormFromResponse = (response) => {
	const data = response?.billing_configuration_data || {}
	const form = {}
	BILLING_FEE_GROUPS.forEach((group) => {
		form[group.key] = {}
		group.fees.forEach((fee) => {
			const raw = data?.[group.wireKey]?.[fee.wireKey]
			form[group.key][fee.key] = raw
				? { amount: raw.amount ?? '', expenseType: raw.expense_type ?? '' }
				: emptyFee()
		})
	})
	return form
}

// Monta o payload no formato que QiTechUpdateBillingConfigurationRequest espera -
// snake_case via wireKey, billingAccountKey nao e enviado (o backend preenche a
// partir do baasId da conta).
const buildBillingPayload = (form) => {
	const toFeeConfig = (fee) => ({
		amount: fee.amount === '' ? null : Number(fee.amount),
		expense_type: fee.expenseType === '' ? null : fee.expenseType,
	})
	const toGroup = (group) =>
		Object.fromEntries(group.fees.map((fee) => [fee.wireKey, toFeeConfig(form[group.key][fee.key])]))
	const billingConfigurationData = Object.fromEntries(
		BILLING_FEE_GROUPS.map((group) => [group.wireKey, toGroup(group)])
	)
	return { billing_configuration_data: billingConfigurationData }
}


// Consultas de transacoes direto na QITech (GET /backoffice/accounts/{publicId}/qitech/...).
// A chave informada e a chave da propria QITech (transacao, lote ou agendamento).
const QITECH_CONSULT_TYPES = [
	{ value: 'pix-transfer', label: 'PIX' },
	{ value: 'ted-transfer', label: 'TED' },
	{ value: 'bill-payment', label: 'Pagamento de conta' },
	{ value: 'pix-batch', label: 'Lote PIX' },
	{ value: 'pix-schedule-batch', label: 'Lote PIX agendado' },
	{ value: 'pix-schedule', label: 'Agendamento PIX' },
	{ value: 'ted-batch', label: 'Lote TED' },
	{ value: 'bill-payment-batch', label: 'Lote de pagamento de contas' },
]

const QITECH_DIRECTION_TYPES = ['pix-transfer', 'ted-transfer']
const QITECH_DIRECTIONS = [
	{ value: 'outgoing', label: 'Saida (outgoing)' },
	{ value: 'incoming', label: 'Entrada (incoming)' },
]

const AccountDetails = () => {
	const { id } = useParams()

	const [account, setAccount] = useState(null)
	const [payingAgentOnboarding, setPayingAgentOnboarding] = useState(null)
	const [authClient, setAuthClient] = useState(null)
	const [allScopes, setAllScopes] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [feedback, setFeedback] = useState('')

	const [selectedScopes, setSelectedScopes] = useState([])
	const [isSavingScopes, setIsSavingScopes] = useState(false)

	const [isCreatingPayingAgent, setIsCreatingPayingAgent] = useState(false)
	const [generatingLinkFor, setGeneratingLinkFor] = useState(null)
	const [isFinalizingOnboarding, setIsFinalizingOnboarding] = useState(false)

	const [pixLimits, setPixLimits] = useState(null)
	const [pixLimitForm, setPixLimitForm] = useState({
		dailyAmountLimit: '',
		nightlyAmountLimit: '',
		selfDailyAmountLimit: '',
		selfNightlyAmountLimit: '',
	})
	const [isLoadingPixLimits, setIsLoadingPixLimits] = useState(true)
	const [isSavingPixLimits, setIsSavingPixLimits] = useState(false)
	const [pixLimitsRequests, setPixLimitsRequests] = useState({ content: [], currentPage: 1, totalPages: 1 })
	const [isLoadingPixLimitsRequests, setIsLoadingPixLimitsRequests] = useState(true)

	const [billingConfig, setBillingConfig] = useState(null)
	const [billingForm, setBillingForm] = useState(null)
	const [isLoadingBilling, setIsLoadingBilling] = useState(true)
	const [isSavingBilling, setIsSavingBilling] = useState(false)
	const [openBillingGroups, setOpenBillingGroups] = useState({})

	const [isBalanceDrawerOpen, setIsBalanceDrawerOpen] = useState(false)
	const [balanceSyncForm, setBalanceSyncForm] = useState({ startDate: '', endDate: '' })
	const [isSyncingBalance, setIsSyncingBalance] = useState(false)

	const [isBaasBalanceDrawerOpen, setIsBaasBalanceDrawerOpen] = useState(false)
	const [baasBalanceSyncForm, setBaasBalanceSyncForm] = useState({ startDate: '', endDate: '' })
	const [isSyncingBaasBalance, setIsSyncingBaasBalance] = useState(false)

	const [isCancelBoletosDrawerOpen, setIsCancelBoletosDrawerOpen] = useState(false)
	const [cancelBoletosForm, setCancelBoletosForm] = useState({ startDate: '', endDate: '' })
	const [isCancelingBoletos, setIsCancelingBoletos] = useState(false)
	const [cancelBoletosResult, setCancelBoletosResult] = useState(null)
	const [isRemoveDiscountDrawerOpen, setIsRemoveDiscountDrawerOpen] = useState(false)
	const [removeDiscountIds, setRemoveDiscountIds] = useState('')
	const [isRemovingDiscounts, setIsRemovingDiscounts] = useState(false)
	const [removeDiscountResult, setRemoveDiscountResult] = useState(null)
	const [qiTechConsultForm, setQiTechConsultForm] = useState({ type: 'pix-transfer', qiTechKey: '', direction: 'outgoing' })
	const [qiTechConsultResult, setQiTechConsultResult] = useState(null)
	const [qiTechConsultError, setQiTechConsultError] = useState('')
	const [isConsultingQiTech, setIsConsultingQiTech] = useState(false)

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		let accountData
		try {
			accountData = await fetchAccountById(id)
			setAccount(accountData)
		} catch (err) {
			setError(err.message)
			setIsLoading(false)
			return
		}

		// auth client e scopes sao opcionais: a conta pode nao ter client vinculado
		const onboardingId = accountData?.payingAgentRequest?.onboardingId
		const [clientResult, scopesResult, onboardingResult] = await Promise.allSettled([
			fetchAccountAuthClient(id),
			fetchAuthScopes(),
			onboardingId ? fetchOnboardingPjById(onboardingId) : Promise.resolve(null),
		])
		setAuthClient(clientResult.status === 'fulfilled' ? clientResult.value : null)
		setAllScopes(scopesResult.status === 'fulfilled' ? scopesResult.value || [] : [])
		setPayingAgentOnboarding(onboardingResult.status === 'fulfilled' ? onboardingResult.value : null)
		setIsLoading(false)
	}, [id])

	useEffect(() => {
		load()
	}, [load])

	const loadPixLimits = useCallback(async () => {
		setIsLoadingPixLimits(true)
		try {
			const limits = await fetchPixLimits(id)
			setPixLimits(limits)
			setPixLimitForm({
				dailyAmountLimit: limits?.dailyAmountLimit ?? '',
				nightlyAmountLimit: limits?.nightlyAmountLimit ?? '',
				selfDailyAmountLimit: limits?.selfDailyAmountLimit ?? '',
				selfNightlyAmountLimit: limits?.selfNightlyAmountLimit ?? '',
			})
		} catch (err) {
			setError(err.message)
		} finally {
			setIsLoadingPixLimits(false)
		}
	}, [id])

	const loadPixLimitsRequests = useCallback(
		async (page = 1) => {
			setIsLoadingPixLimitsRequests(true)
			try {
				const paged = await fetchPixLimitsRequests(id, { page })
				setPixLimitsRequests(paged)
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoadingPixLimitsRequests(false)
			}
		},
		[id]
	)

	const loadBillingConfiguration = useCallback(async () => {
		setIsLoadingBilling(true)
		try {
			const response = await fetchBillingConfiguration(id)
			setBillingConfig(response)
			setBillingForm(buildBillingFormFromResponse(response))
		} catch (err) {
			setError(err.message)
		} finally {
			setIsLoadingBilling(false)
		}
	}, [id])

	useEffect(() => {
		loadPixLimits()
		loadPixLimitsRequests(1)
		loadBillingConfiguration()
	}, [loadPixLimits, loadPixLimitsRequests, loadBillingConfiguration])

	const currentScopeIds = useMemo(
		() => new Set((authClient?.scopes || []).map((scope) => scope.id)),
		[authClient]
	)

	const groupedScopes = useMemo(() => {
		const groups = {}
		allScopes.forEach((scope) => {
			const key = scope.context || 'UNKNOWN'
			groups[key] = groups[key] || []
			groups[key].push(scope)
		})
		return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
	}, [allScopes])

	const handlePixLimitFormChange = (e) => {
		const { name, value } = e.target
		setPixLimitForm((prev) => ({ ...prev, [name]: value }))
	}

	const handleRequestPixLimitChange = async (e) => {
		e.preventDefault()
		setIsSavingPixLimits(true)
		setError('')
		setFeedback('')
		try {
			await requestPixLimitChange(id, {
				dailyAmountLimit: Number(pixLimitForm.dailyAmountLimit),
				nightlyAmountLimit: Number(pixLimitForm.nightlyAmountLimit),
				selfDailyAmountLimit: Number(pixLimitForm.selfDailyAmountLimit),
				selfNightlyAmountLimit: Number(pixLimitForm.selfNightlyAmountLimit),
			})
			setFeedback('Solicitacao de alteracao de limites Pix enviada')
			await loadPixLimits()
			await loadPixLimitsRequests(1)
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSavingPixLimits(false)
		}
	}

	const handleBillingFeeChange = (groupKey, feeKey, field, value) => {
		setBillingForm((prev) => ({
			...prev,
			[groupKey]: {
				...prev[groupKey],
				[feeKey]: {
					...prev[groupKey][feeKey],
					[field]: value,
				},
			},
		}))
	}

	const toggleBillingGroup = (groupKey) => {
		setOpenBillingGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
	}

	const handleSaveBillingConfiguration = async (e) => {
		e.preventDefault()
		setIsSavingBilling(true)
		setError('')
		setFeedback('')
		try {
			await updateBillingConfiguration(id, buildBillingPayload(billingForm))
			setFeedback('Configuracao de tarifas atualizada')
			await loadBillingConfiguration()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSavingBilling(false)
		}
	}

	const handleSyncBalance = async (e) => {
		e.preventDefault()
		setIsSyncingBalance(true)
		setError('')
		setFeedback('')
		try {
			await syncAccountDailyBalances(id, balanceSyncForm)
			setFeedback('Sincronizacao de saldo diario concluida')
			setIsBalanceDrawerOpen(false)
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSyncingBalance(false)
		}
	}

	const handleSyncBaasBalance = async (e) => {
		e.preventDefault()
		setIsSyncingBaasBalance(true)
		setError('')
		setFeedback('')
		try {
			const result = await syncAccountBaasBalance(id, baasBalanceSyncForm)
			setFeedback(`Sincronizacao de saldo BaaS concluida - ${result?.updatedPostings ?? 0} posting(s) atualizado(s)`)
			setIsBaasBalanceDrawerOpen(false)
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSyncingBaasBalance(false)
		}
	}

	const openCancelBoletosDrawer = () => {
		setCancelBoletosResult(null)
		setIsCancelBoletosDrawerOpen(true)
	}

	const handleQiTechConsult = async (e) => {
		e.preventDefault()
		const qiTechKey = qiTechConsultForm.qiTechKey.trim()
		if (!qiTechKey) {
			setQiTechConsultError('Informe a chave da QITech')
			return
		}
		const direction = QITECH_DIRECTION_TYPES.includes(qiTechConsultForm.type) ? qiTechConsultForm.direction : undefined
		setIsConsultingQiTech(true)
		setQiTechConsultError('')
		setQiTechConsultResult(null)
		try {
			setQiTechConsultResult(await consultTransactionOnQiTech(id, qiTechConsultForm.type, qiTechKey, direction))
		} catch (err) {
			setQiTechConsultError(err.message)
		} finally {
			setIsConsultingQiTech(false)
		}
	}

	const openRemoveDiscountDrawer = () => {
		setRemoveDiscountResult(null)
		setIsRemoveDiscountDrawerOpen(true)
	}

	// Aceita os public ids separados por quebra de linha, virgula, ponto e virgula ou espaco.
	const parseBoletoIds = (text) => [...new Set(text.split(/[\s,;]+/).map((value) => value.trim()).filter(Boolean))]

	const handleRemoveBoletoDiscounts = async (e) => {
		e.preventDefault()
		const boletoIds = parseBoletoIds(removeDiscountIds)
		if (boletoIds.length === 0) {
			setError('Informe ao menos um id de boleto')
			return
		}
		const confirmado = window.confirm(
			`Os descontos de ${boletoIds.length} boleto(s) serao removidos na QITech e no KSI Pay. Confirma?`
		)
		if (!confirmado) return

		setIsRemovingDiscounts(true)
		setRemoveDiscountResult(null)
		setError('')
		setFeedback('')
		try {
			const result = await removeAccountBoletoDiscountsOnQiTech(id, boletoIds)
			setRemoveDiscountResult(result)
			setFeedback(
				`Remocao de descontos concluida - ${result?.updated ?? 0} atualizado(s), ` +
					`${result?.skipped ?? 0} ignorado(s), ${result?.failed ?? 0} falha(s) de ${result?.total ?? 0} boleto(s)`
			)
			if (!result?.failed) {
				setRemoveDiscountIds('')
				setIsRemoveDiscountDrawerOpen(false)
			}
		} catch (err) {
			setError(err.message)
		} finally {
			setIsRemovingDiscounts(false)
		}
	}

	const handleCancelBoletosOnQiTech = async (e) => {
		e.preventDefault()
		const confirmado = window.confirm(
			'Sera solicitada na QITech a baixa de todos os boletos cancelados desta conta ' +
				'atualizados no periodo informado. Confirma?'
		)
		if (!confirmado) return

		setIsCancelingBoletos(true)
		setCancelBoletosResult(null)
		setError('')
		setFeedback('')
		try {
			const result = await cancelAccountBoletosOnQiTech(id, cancelBoletosForm)
			setCancelBoletosResult(result)
			setFeedback(
				`Cancelamento na QITech concluido - ${result?.cancelled ?? 0} baixado(s), ` +
					`${result?.skipped ?? 0} ignorado(s), ${result?.failed ?? 0} falha(s) de ${result?.total ?? 0} boleto(s)`
			)
			if (!result?.failed) setIsCancelBoletosDrawerOpen(false)
		} catch (err) {
			setError(err.message)
		} finally {
			setIsCancelingBoletos(false)
		}
	}

	const handleCreatePayingAgentAccount = async () => {
		const confirmado = window.confirm(
			'Sera aberto um onboarding PJ com os dados da conta MASTER para criar a ' +
				'conta de pagadoria. A biometria dos representantes legais precisa ser ' +
				'refeita neste onboarding. Confirma?'
		)
		if (!confirmado) return

		setIsCreatingPayingAgent(true)
		setError('')
		setFeedback('')
		try {
			await createPayingAgentAccount(id)
			setFeedback(
				'Onboarding da pagadoria criado. Refaca a biometria dos representantes ' +
					'legais e finalize o onboarding para a conta ser criada.'
			)
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsCreatingPayingAgent(false)
		}
	}

	const handleGenerateBiometricLink = async (partner) => {
		setGeneratingLinkFor(partner.id)
		setError('')
		setFeedback('')
		try {
			await generateBiometricLink(
				account.payingAgentRequest.onboardingId,
				partner.id,
				partner.legalRepresentative
			)
			setFeedback('Link de biometria gerado')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setGeneratingLinkFor(null)
		}
	}

	const handleFinalizeOnboarding = async () => {
		const confirmado = window.confirm(
			'Finalizar o onboarding da pagadoria? Dependendo do status atual isso envia ' +
				'os dados para o BaaS e reserva a conta.'
		)
		if (!confirmado) return

		setIsFinalizingOnboarding(true)
		setError('')
		setFeedback('')
		try {
			await finalizeOnboarding(account.payingAgentRequest.onboardingId)
			setFeedback('Onboarding finalizado')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsFinalizingOnboarding(false)
		}
	}

	const toggleScopeSelection = (scopeId) => {
		setSelectedScopes((prev) =>
			prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId]
		)
	}

	const handleScopeAction = async (action) => {
		if (selectedScopes.length === 0) {
			setError('Selecione ao menos um scope')
			return
		}

		setIsSavingScopes(true)
		setError('')
		setFeedback('')
		try {
			if (action === 'add') {
				await addAccountScopes(id, selectedScopes)
				setFeedback(`${selectedScopes.length} scope(s) adicionado(s)`)
			} else {
				await removeAccountScopes(id, selectedScopes)
				setFeedback(`${selectedScopes.length} scope(s) removido(s)`)
			}
			setSelectedScopes([])
			const refreshed = await fetchAccountAuthClient(id).catch(() => null)
			setAuthClient(refreshed)
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSavingScopes(false)
		}
	}

	if (isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader size="lg" />
			</div>
		)
	}

	return (
		<div>
			<Link
				to="/contas"
				className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
			>
				<ArrowLeft className="mr-1 h-4 w-4" />
				Voltar para contas
			</Link>

			<PageHeader
				title={account?.accountHolder?.name || 'Conta'}
				subtitle={`GET /backoffice/accounts/${id}`}
				actions={
					<Button
						variant="secondary"
						onClick={() => {
							load()
							loadPixLimits()
							loadPixLimitsRequests(1)
							loadBillingConfiguration()
						}}
					>
						<RefreshCw className="h-4 w-4" />
						Recarregar
					</Button>
				}
			/>

			<Alert onDismiss={() => setError('')}>{error}</Alert>
			<Alert type="success" onDismiss={() => setFeedback('')}>
				{feedback}
			</Alert>

			{!account ? (
				<EmptyState message="Conta nao encontrada" />
			) : (
				<div className="space-y-6">
					<Card
						title="Dados da conta"
						actions={
							<div className="flex flex-wrap gap-2">
								<Button variant="secondary" onClick={() => setIsBalanceDrawerOpen(true)}>
									<History className="h-4 w-4" />
									Sincronizar saldo
								</Button>
								<Button variant="secondary" onClick={() => setIsBaasBalanceDrawerOpen(true)}>
									<RefreshCw className="h-4 w-4" />
									Sincronizar saldo BaaS
								</Button>
								<Button variant="secondary" onClick={openCancelBoletosDrawer}>
									<Ban className="h-4 w-4" />
									Cancelar boletos na QI
								</Button>
								<Button variant="secondary" onClick={openRemoveDiscountDrawer}>
									<BadgePercent className="h-4 w-4" />
									Remover descontos na QI
								</Button>
							</div>
						}
					>
						<DetailGrid>
							<DetailItem label="ID">
								<CopyableId value={account.id} />
							</DetailItem>
							<DetailItem label="Numero">{account.number}</DetailItem>
							<DetailItem label="Agencia">{account.branch}</DetailItem>
							<DetailItem label="Saldo">{formatCurrency(account.balance)}</DetailItem>
							<DetailItem label="Titular">{account.accountHolder?.name}</DetailItem>
							<DetailItem label="Documento">
								{formatDocument(account.accountHolder?.document)}
							</DetailItem>
							<DetailItem label="Tipo de cliente">
								{account.accountHolder?.type && (
									<Badge tone="blue">{account.accountHolder.type}</Badge>
								)}
							</DetailItem>
							<DetailItem label="Onboarding">
								{account.onboarding?.id ? (
									<Link
										to={`/onboardings/${(account.accountHolder?.type || 'pj').toLowerCase()}/${account.onboarding.id}`}
										className="text-primary-600 hover:underline"
									>
										{account.onboarding.id}
									</Link>
								) : null}
							</DetailItem>
							<DetailItem label="Plano">{account.plan?.id}</DetailItem>
						</DetailGrid>
					</Card>

					<Card
						title="Pagadoria"
						description="Pagadoria vinculada a esta conta"
						actions={
							!account.payingAgent && !account.payingAgentRequest ? (
								<Button onClick={handleCreatePayingAgentAccount} disabled={isCreatingPayingAgent}>
									{isCreatingPayingAgent ? (
										<Loader size="sm" />
									) : (
										<Plus className="h-4 w-4" />
									)}
									Solicitar conta de pagadoria
								</Button>
							) : !account.payingAgent && account.payingAgentRequest ? (
								<Button onClick={handleFinalizeOnboarding} disabled={isFinalizingOnboarding}>
									{isFinalizingOnboarding ? (
										<Loader size="sm" />
									) : (
										<Send className="h-4 w-4" />
									)}
									Finalizar onboarding
								</Button>
							) : null
						}
					>
						{!account.payingAgent && account.payingAgentRequest ? (
							<div className="space-y-4">
								<div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
									Solicitacao em andamento. A conta de pagadoria so e criada depois que a
									biometria dos representantes legais for refeita e o onboarding for
									finalizado.
								</div>
								<DetailGrid cols={2}>
									<DetailItem label="Onboarding">
										<Link
											to={`/onboardings/pj/${account.payingAgentRequest.onboardingId}`}
											className="text-primary-600 hover:underline"
										>
											{account.payingAgentRequest.onboardingId}
										</Link>
									</DetailItem>
									<DetailItem label="Situacao">
										{payingAgentOnboarding?.status && (
											<Badge tone="yellow">{payingAgentOnboarding.status}</Badge>
										)}
									</DetailItem>
									<DetailItem label="Razao social">{payingAgentOnboarding?.legalName}</DetailItem>
									<DetailItem label="CNPJ">
										{formatDocument(payingAgentOnboarding?.document)}
									</DetailItem>
									<DetailItem label="Solicitada em">
										{formatDateTime(payingAgentOnboarding?.createdAt)}
									</DetailItem>
								</DetailGrid>
								{(payingAgentOnboarding?.partners || []).length > 0 && (
									<div>
										<p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
											Biometria dos representantes legais
										</p>
										<ul className="space-y-2 text-sm text-gray-700">
											{payingAgentOnboarding.partners.map((partner) => (
												<li key={partner.id} className="flex flex-wrap items-center gap-2">
													<span>
														{partner.name} — {formatDocument(partner.document)}
													</span>
													{partner.legalRepresentative && (
														<Badge tone="blue">representante legal</Badge>
													)}
													{partner.biometric && (
														<Badge tone={statusTone(partner.biometric.status)}>
															{partner.biometric.status}
														</Badge>
													)}
													{(partner.biometric?.documents || []).map((doc) => (
														<Badge key={doc.id} tone={statusTone(doc.result)}>
															{doc.documentType}: {doc.result}
														</Badge>
													))}
													{partner.biometricLink ? (
														<CopyButton value={partner.biometricLink} label="Copiar link" />
													) : (
														<button
															type="button"
															onClick={() => handleGenerateBiometricLink(partner)}
															disabled={generatingLinkFor === partner.id}
															className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
														>
															<LinkIcon className="h-3.5 w-3.5" />
															{generatingLinkFor === partner.id ? 'Gerando...' : 'Gerar link'}
														</button>
													)}
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						) : !account.payingAgent ? (
							<EmptyState message="Esta conta nao possui pagadoria vinculada" />
						) : (
							<DetailGrid cols={2}>
								<DetailItem label="ID da pagadoria">
									<CopyableId value={account.payingAgent.id} />
								</DetailItem>
								<DetailItem label="Situacao">
									<Badge tone={account.payingAgent.active ? 'green' : 'red'}>
										{account.payingAgent.active ? 'Ativa' : 'Inativa'}
									</Badge>
								</DetailItem>
								<DetailItem label="Criada em">
									{formatDateTime(account.payingAgent.createdAt)}
								</DetailItem>
								<DetailItem label="Conta de comissao">
									{account.payingAgent.commissionAccount ? (
										<Link
											to={`/contas/${account.payingAgent.commissionAccount.id}`}
											className="text-primary-600 hover:underline"
										>
											{account.payingAgent.commissionAccount.number || '—'}
											{account.payingAgent.commissionAccount.holderName
												? ` — ${account.payingAgent.commissionAccount.holderName}`
												: ''}
										</Link>
									) : (
										'—'
									)}
								</DetailItem>
								<DetailItem label="Detalhes">
									{account.payingAgent.onboardingId ? (
										<Link
											to={`/pagadoria/${account.payingAgent.onboardingId}`}
											className="text-primary-600 hover:underline"
										>
											Abrir detalhes da pagadoria
										</Link>
									) : (
										'Pagadoria criada sem onboarding (associacao manual)'
									)}
								</DetailItem>
							</DetailGrid>
						)}
					</Card>

					<Card title="Limites Pix" description={`GET / POST /backoffice/accounts/${id}/pix/limits`}>
						{isLoadingPixLimits ? (
							<div className="flex h-32 items-center justify-center">
								<Loader size="lg" />
							</div>
						) : !pixLimits ? (
							<EmptyState message="Nao foi possivel carregar os limites Pix" />
						) : (
							<div className="space-y-6">
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200 text-sm">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
													Categoria
												</th>
												<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
													Limite
												</th>
												<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
													Utilizado
												</th>
												<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
													Percentual
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200">
											<tr>
												<td className="px-4 py-2 text-gray-900">Diario</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.dailyAmountLimit)}</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.dailyAmountUsed)}</td>
												<td className="px-4 py-2">{formatPercentage(pixLimits.dailyAmountPercentage)}</td>
											</tr>
											<tr>
												<td className="px-4 py-2 text-gray-900">Noturno</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.nightlyAmountLimit)}</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.nightlyAmountUsed)}</td>
												<td className="px-4 py-2">{formatPercentage(pixLimits.nightlyAmountPercentage)}</td>
											</tr>
											<tr>
												<td className="px-4 py-2 text-gray-900">Diario (mesma titularidade)</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.selfDailyAmountLimit)}</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.selfDailyAmountUsed)}</td>
												<td className="px-4 py-2">{formatPercentage(pixLimits.selfDailyAmountPercentage)}</td>
											</tr>
											<tr>
												<td className="px-4 py-2 text-gray-900">Noturno (mesma titularidade)</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.selfNightlyAmountLimit)}</td>
												<td className="px-4 py-2">{formatCurrency(pixLimits.selfNightlyAmountUsed)}</td>
												<td className="px-4 py-2">{formatPercentage(pixLimits.selfNightlyAmountPercentage)}</td>
											</tr>
										</tbody>
									</table>
								</div>

								<form onSubmit={handleRequestPixLimitChange} className="space-y-4">
									<p className="text-sm font-semibold text-gray-900">Solicitar alteracao de limites</p>
									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
										<TextInput
											id="dailyAmountLimit"
											name="dailyAmountLimit"
											label="Limite diario"
											type="number"
											step="0.01"
											min="0"
											required
											value={pixLimitForm.dailyAmountLimit}
											onChange={handlePixLimitFormChange}
										/>
										<TextInput
											id="nightlyAmountLimit"
											name="nightlyAmountLimit"
											label="Limite noturno"
											type="number"
											step="0.01"
											min="0"
											required
											value={pixLimitForm.nightlyAmountLimit}
											onChange={handlePixLimitFormChange}
										/>
										<TextInput
											id="selfDailyAmountLimit"
											name="selfDailyAmountLimit"
											label="Limite diario (mesma titularidade)"
											type="number"
											step="0.01"
											min="0"
											required
											value={pixLimitForm.selfDailyAmountLimit}
											onChange={handlePixLimitFormChange}
										/>
										<TextInput
											id="selfNightlyAmountLimit"
											name="selfNightlyAmountLimit"
											label="Limite noturno (mesma titularidade)"
											type="number"
											step="0.01"
											min="0"
											required
											value={pixLimitForm.selfNightlyAmountLimit}
											onChange={handlePixLimitFormChange}
										/>
									</div>
									<Button type="submit" disabled={isSavingPixLimits}>
										{isSavingPixLimits ? <Loader size="sm" /> : <Send className="h-4 w-4" />}
										Solicitar alteracao
									</Button>
								</form>

								<div>
									<p className="mb-2 text-sm font-semibold text-gray-900">Historico de solicitacoes</p>
									{isLoadingPixLimitsRequests ? (
										<div className="flex h-24 items-center justify-center">
											<Loader size="lg" />
										</div>
									) : pixLimitsRequests.content.length === 0 ? (
										<EmptyState message="Nenhuma solicitacao de limite Pix" />
									) : (
										<div className="overflow-hidden rounded-lg border border-gray-200">
											<table className="min-w-full divide-y divide-gray-200 text-sm">
												<thead className="bg-gray-50">
													<tr>
														<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
															Tipo de limite
														</th>
														<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
															Valor
														</th>
														<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
															Status
														</th>
														<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
															Solicitado em
														</th>
													</tr>
												</thead>
												<tbody className="divide-y divide-gray-200">
													{pixLimitsRequests.content.map((request) => (
														<tr key={request.requestKey}>
															<td className="px-4 py-2 text-gray-900">{request.limitType}</td>
															<td className="px-4 py-2">{formatCurrency(request.amountLimit)}</td>
															<td className="px-4 py-2">
																<Badge tone={statusTone(request.requestStatus)}>
																	{request.requestStatus}
																</Badge>
															</td>
															<td className="px-4 py-2 text-gray-500">{formatDateTime(request.createdAt)}</td>
														</tr>
													))}
												</tbody>
											</table>
											<Pagination
												currentPage={pixLimitsRequests.currentPage}
												totalPages={pixLimitsRequests.totalPages}
												onPageChange={loadPixLimitsRequests}
											/>
										</div>
									)}
								</div>
							</div>
						)}
					</Card>

					<Card
						title="Configuracao de tarifas"
						description={`GET / PUT /backoffice/accounts/${id}/billing-configuration`}
					>
						{isLoadingBilling ? (
							<div className="flex h-32 items-center justify-center">
								<Loader size="lg" />
							</div>
						) : !billingForm ? (
							<EmptyState message="Nao foi possivel carregar a configuracao de tarifas" />
						) : (
							<form onSubmit={handleSaveBillingConfiguration} className="space-y-4">
								<DetailGrid cols={2}>
									<DetailItem label="Status">
										{billingConfig?.status && (
											<Badge tone={statusTone(billingConfig.status)}>{billingConfig.status}</Badge>
										)}
									</DetailItem>
									<DetailItem label="Proxima manutencao da conta">
										{formatDateTime(billingConfig?.account_maintenance_next_execution)}
									</DetailItem>
								</DetailGrid>

								{BILLING_FEE_GROUPS.map((group) => (
									<div key={group.key} className="rounded-md border border-gray-200">
										<button
											type="button"
											onClick={() => toggleBillingGroup(group.key)}
											className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-gray-900"
										>
											{group.title}
											{openBillingGroups[group.key] ? (
												<ChevronDown className="h-4 w-4" />
											) : (
												<ChevronRight className="h-4 w-4" />
											)}
										</button>
										{openBillingGroups[group.key] && (
											<div className="overflow-x-auto border-t border-gray-200">
												<table className="min-w-full divide-y divide-gray-200 text-sm">
													<thead className="bg-gray-50">
														<tr>
															<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
																Tarifa
															</th>
															<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
																Valor (R$)
															</th>
															<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
																Tipo de despesa
															</th>
														</tr>
													</thead>
													<tbody className="divide-y divide-gray-200">
														{group.fees.map((fee) => (
															<tr key={fee.key}>
																<td className="px-4 py-2 text-gray-900">{fee.label}</td>
																<td className="px-4 py-2">
																	<TextInput
																		type="number"
																		step="0.01"
																		min="0"
																		value={billingForm[group.key][fee.key].amount}
																		onChange={(e) =>
																			handleBillingFeeChange(group.key, fee.key, 'amount', e.target.value)
																		}
																	/>
																</td>
																<td className="px-4 py-2">
																	<TextInput
																		type="text"
																		value={billingForm[group.key][fee.key].expenseType}
																		onChange={(e) =>
																			handleBillingFeeChange(group.key, fee.key, 'expenseType', e.target.value)
																		}
																	/>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}
									</div>
								))}

								<Button type="submit" disabled={isSavingBilling}>
									{isSavingBilling ? <Loader size="sm" /> : <Save className="h-4 w-4" />}
									Salvar configuracao de tarifas
								</Button>
							</form>
						)}
					</Card>

					<Card
						title="Consultar transacao na QITech"
						description={`GET /backoffice/accounts/${id}/qitech/... — retorna o JSON da QITech`}
					>
						<form onSubmit={handleQiTechConsult} className="space-y-4">
							<div className="grid gap-4 md:grid-cols-3">
								<Select
									id="qitech-consult-type"
									label="Tipo"
									options={QITECH_CONSULT_TYPES}
									value={qiTechConsultForm.type}
									onChange={(e) => setQiTechConsultForm((prev) => ({ ...prev, type: e.target.value }))}
								/>
								<TextInput
									id="qitech-consult-key"
									label="Chave na QITech"
									placeholder="ex.: pix_transfer_key, ted_key, batch key, schedule key"
									value={qiTechConsultForm.qiTechKey}
									onChange={(e) => setQiTechConsultForm((prev) => ({ ...prev, qiTechKey: e.target.value }))}
								/>
								{QITECH_DIRECTION_TYPES.includes(qiTechConsultForm.type) && (
									<Select
										id="qitech-consult-direction"
										label="Direcao"
										options={QITECH_DIRECTIONS}
										value={qiTechConsultForm.direction}
										onChange={(e) => setQiTechConsultForm((prev) => ({ ...prev, direction: e.target.value }))}
									/>
								)}
							</div>
							<Button type="submit" disabled={isConsultingQiTech}>
								{isConsultingQiTech ? <Loader size="sm" /> : <Search className="h-4 w-4" />}
								Consultar
							</Button>
						</form>
						{qiTechConsultError && (
							<div className="mt-4">
								<Alert onDismiss={() => setQiTechConsultError('')}>{qiTechConsultError}</Alert>
							</div>
						)}
						{qiTechConsultResult && (
							<div className="mt-4 space-y-2">
								<div className="flex justify-end">
									<CopyButton value={JSON.stringify(qiTechConsultResult, null, 2)} label="Copiar JSON" />
								</div>
								<JsonViewer data={qiTechConsultResult} />
							</div>
						)}
					</Card>

					<Card
						title="Auth client"
						description={`GET /backoffice/accounts/${id}/auth-client`}
					>
						{!authClient ? (
							<EmptyState message="Nenhum auth client vinculado a esta conta" />
						) : (
							<DetailGrid cols={2}>
								<DetailItem label="Client ID">
									<CopyableId value={authClient.clientId} />
								</DetailItem>
								<DetailItem label="Client Secret">
									<CopyableId value={authClient.clientSecret} />
								</DetailItem>
								<DetailItem label="Tenant ID">
									<CopyableId value={authClient.tenantId} />
								</DetailItem>
								<DetailItem label="Nome">{authClient.clientName}</DetailItem>
								<DetailItem label="Grant type">{authClient.grantType}</DetailItem>
								<DetailItem label="Redirect URIs">{authClient.redirectUris}</DetailItem>
								<DetailItem label="Habilitado">
									<Badge tone={authClient.enabled ? 'green' : 'red'}>
										{authClient.enabled ? 'Sim' : 'Nao'}
									</Badge>
								</DetailItem>
								<DetailItem label="Criado em">
									{formatDateTime(authClient.createdAt)}
								</DetailItem>
							</DetailGrid>
						)}
					</Card>

					<Card
						title="Scopes"
						description="POST / DELETE /backoffice/accounts/{publicId}/auth-client/scopes"
						actions={
							<>
								<Button
									variant="secondary"
									disabled={isSavingScopes || selectedScopes.length === 0}
									onClick={() => handleScopeAction('add')}
								>
									<Plus className="h-4 w-4" />
									Adicionar ({selectedScopes.length})
								</Button>
								<Button
									variant="danger"
									disabled={isSavingScopes || selectedScopes.length === 0}
									onClick={() => handleScopeAction('remove')}
								>
									<Minus className="h-4 w-4" />
									Remover ({selectedScopes.length})
								</Button>
							</>
						}
					>
						{allScopes.length === 0 ? (
							<EmptyState message="Nenhum scope disponivel" />
						) : (
							<div className="space-y-6">
								{groupedScopes.map(([context, scopes]) => (
									<div key={context}>
										<p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
											{context}
										</p>
										<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
											{scopes.map((scope) => {
												const isAssigned = currentScopeIds.has(scope.id)
												return (
													<label
														key={scope.id}
														className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${
															isAssigned
																? 'border-green-200 bg-green-50'
																: 'border-gray-200 bg-white'
														}`}
													>
														<input
															type="checkbox"
															checked={selectedScopes.includes(scope.id)}
															onChange={() => toggleScopeSelection(scope.id)}
															className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
														/>
														<span className="flex-1">
															<span className="flex flex-wrap items-center gap-2">
																<code className="font-mono text-xs text-gray-800">
																	{scope.scopeName}
																</code>
																{isAssigned && <Badge tone="green">ativo</Badge>}
																{scope.defaultScope && <Badge tone="blue">padrao</Badge>}
															</span>
															{scope.description && (
																<span className="mt-1 block text-xs text-gray-500">
																	{scope.description}
																</span>
															)}
														</span>
													</label>
												)
											})}
										</div>
									</div>
								))}
							</div>
						)}
					</Card>
				</div>
			)}

			<Drawer
				open={isBalanceDrawerOpen}
				title="Sincronizar saldo diario"
				description={`POST /backoffice/accounts/${id}/daily-balances/generate`}
				onClose={() => setIsBalanceDrawerOpen(false)}
			>
				<form onSubmit={handleSyncBalance} className="space-y-4">
					<TextInput
						id="balance-sync-start-date"
						label="Data inicial"
						type="date"
						value={balanceSyncForm.startDate}
						onChange={(e) => setBalanceSyncForm((prev) => ({ ...prev, startDate: e.target.value }))}
						required
					/>
					<TextInput
						id="balance-sync-end-date"
						label="Data final"
						type="date"
						value={balanceSyncForm.endDate}
						onChange={(e) => setBalanceSyncForm((prev) => ({ ...prev, endDate: e.target.value }))}
						required
					/>
					<p className="text-xs text-gray-500">
						Recalcula o saldo de fechamento de cada dia do intervalo a partir do razao (ledger)
						desta conta.
					</p>
					<div className="flex justify-end gap-2 pt-2">
						<Button type="button" variant="secondary" onClick={() => setIsBalanceDrawerOpen(false)}>
							Cancelar
						</Button>
						<Button type="submit" disabled={isSyncingBalance}>
							{isSyncingBalance ? <Loader size="sm" /> : <History className="h-4 w-4" />}
							Sincronizar
						</Button>
					</div>
				</form>
			</Drawer>

			<Drawer
				open={isBaasBalanceDrawerOpen}
				title="Sincronizar saldo BaaS"
				description={`POST /backoffice/accounts/${id}/balance/sync`}
				onClose={() => setIsBaasBalanceDrawerOpen(false)}
			>
				<form onSubmit={handleSyncBaasBalance} className="space-y-4">
					<TextInput
						id="baas-balance-sync-start-date"
						label="Data inicial"
						type="date"
						value={baasBalanceSyncForm.startDate}
						onChange={(e) => setBaasBalanceSyncForm((prev) => ({ ...prev, startDate: e.target.value }))}
						required
					/>
					<TextInput
						id="baas-balance-sync-end-date"
						label="Data final"
						type="date"
						value={baasBalanceSyncForm.endDate}
						onChange={(e) => setBaasBalanceSyncForm((prev) => ({ ...prev, endDate: e.target.value }))}
						required
					/>
					<p className="text-xs text-gray-500">
						Busca o extrato desta conta no BaaS (QITech) para o intervalo e grava o saldo apos cada
						transacao nos postings correspondentes.
					</p>
					<div className="flex justify-end gap-2 pt-2">
						<Button type="button" variant="secondary" onClick={() => setIsBaasBalanceDrawerOpen(false)}>
							Cancelar
						</Button>
						<Button type="submit" disabled={isSyncingBaasBalance}>
							{isSyncingBaasBalance ? <Loader size="sm" /> : <RefreshCw className="h-4 w-4" />}
							Sincronizar
						</Button>
					</div>
				</form>
			</Drawer>

			<Drawer
				open={isCancelBoletosDrawerOpen}
				title="Cancelar boletos na QITech"
				description="POST /backoffice/boletos/qitech/cancel"
				onClose={() => setIsCancelBoletosDrawerOpen(false)}
			>
				<form onSubmit={handleCancelBoletosOnQiTech} className="space-y-4">
					<TextInput
						id="cancel-boletos-start-date"
						label="Data inicial"
						type="date"
						value={cancelBoletosForm.startDate}
						onChange={(e) => setCancelBoletosForm((prev) => ({ ...prev, startDate: e.target.value }))}
						required
					/>
					<TextInput
						id="cancel-boletos-end-date"
						label="Data final"
						type="date"
						value={cancelBoletosForm.endDate}
						onChange={(e) => setCancelBoletosForm((prev) => ({ ...prev, endDate: e.target.value }))}
						required
					/>
					<p className="text-xs text-gray-500">
						Busca os boletos desta conta com status cancelado e data de atualizacao no intervalo e
						solicita a baixa de cada um na QITech. Os boletos no KSI Pay nao sao alterados; boletos
						sem registro na QITech sao ignorados.
					</p>
					{cancelBoletosResult?.failures?.length > 0 && (
						<div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
							<p className="mb-2 font-medium">
								{cancelBoletosResult.failed} boleto(s) com falha na QITech:
							</p>
							<ul className="max-h-60 space-y-1 overflow-y-auto">
								{cancelBoletosResult.failures.map((failure) => (
									<li key={failure.boletoPublicId}>
										<span className="font-mono">{failure.boletoPublicId}</span>
										{failure.message ? ` - ${failure.message}` : ''}
									</li>
								))}
							</ul>
						</div>
					)}
					<div className="flex justify-end gap-2 pt-2">
						<Button type="button" variant="secondary" onClick={() => setIsCancelBoletosDrawerOpen(false)}>
							Fechar
						</Button>
						<Button type="submit" disabled={isCancelingBoletos}>
							{isCancelingBoletos ? <Loader size="sm" /> : <Ban className="h-4 w-4" />}
							Cancelar na QITech
						</Button>
					</div>
				</form>
			</Drawer>

			<Drawer
				open={isRemoveDiscountDrawerOpen}
				title="Remover descontos dos boletos na QITech"
				description="POST /backoffice/boletos/qitech/discount/remove"
				onClose={() => setIsRemoveDiscountDrawerOpen(false)}
			>
				<form onSubmit={handleRemoveBoletoDiscounts} className="space-y-4">
					<div>
						<label htmlFor="remove-discount-ids" className="mb-1 block text-sm font-medium text-gray-700">
							IDs dos boletos
						</label>
						<textarea
							id="remove-discount-ids"
							rows={8}
							className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
							placeholder="Um id por linha (ou separados por virgula)"
							value={removeDiscountIds}
							onChange={(e) => setRemoveDiscountIds(e.target.value)}
							required
						/>
						<p className="mt-1 text-xs text-gray-500">
							{parseBoletoIds(removeDiscountIds).length} boleto(s) informado(s)
						</p>
					</div>
					<p className="text-xs text-gray-500">
						Para cada boleto desta conta, envia a instrucao de desconto na QITech com os descontos nulos e,
						se aceita, limpa o tipo, o valor e a data limite do desconto no KSI Pay. Boletos sem registro na
						QITech sao ignorados.
					</p>
					{removeDiscountResult?.failures?.length > 0 && (
						<div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
							<p className="mb-2 font-medium">
								{removeDiscountResult.failed} boleto(s) com falha:
							</p>
							<ul className="max-h-60 space-y-1 overflow-y-auto">
								{removeDiscountResult.failures.map((failure) => (
									<li key={failure.boletoPublicId}>
										<span className="font-mono">{failure.boletoPublicId}</span>
										{failure.message ? ` - ${failure.message}` : ''}
									</li>
								))}
							</ul>
						</div>
					)}
					<div className="flex justify-end gap-2 pt-2">
						<Button type="button" variant="secondary" onClick={() => setIsRemoveDiscountDrawerOpen(false)}>
							Fechar
						</Button>
						<Button type="submit" disabled={isRemovingDiscounts}>
							{isRemovingDiscounts ? <Loader size="sm" /> : <BadgePercent className="h-4 w-4" />}
							Remover descontos
						</Button>
					</div>
				</form>
			</Drawer>
		</div>
	)
}

export default AccountDetails
